import type { Gender, ListPractitionersQuery, PractitionerType, SessionFormat } from "cmw-client";
import { GENDER_OPTIONS, PRACTITIONER_TYPE_OPTIONS, SESSION_FORMAT_LABELS } from "./options";

/**
 * Every control on the bar and in the panel, in the order they read. Each one is a list — the API
 * ORs within a filter and ANDs across them — so the whole state is one uniform shape and the
 * controls need nothing but a key.
 */
export const FILTER_KEYS = [
  "gender",
  "practitionerType",
  "sessionFormat",
  "focusAreas",
  "therapeuticApproaches",
  "culturalBackground",
  "languages",
  "ageGroups",
  "clientTypes",
  "fundingPathways",
  "thirdPartySchemes",
] as const satisfies readonly (keyof ListPractitionersQuery)[];

export type FilterKey = (typeof FILTER_KEYS)[number];

/**
 * The directory's filter state. Values are the slugs (or, for the two enum filters, the enum
 * members) the API takes, so the state *is* the query — `toQuery` only adds the origin.
 */
export type DirectoryFilters = Partial<Record<FilterKey, string[]>>;

export const NO_FILTERS: DirectoryFilters = {};

/** Drops the cleared controls, so an empty filter never reaches the query key or the wire. */
export const activeFilters = (filters: DirectoryFilters): DirectoryFilters =>
  Object.fromEntries(FILTER_KEYS.flatMap((key) => ((filters[key]?.length ?? 0) > 0 ? [[key, filters[key]!]] : [])));

export const filterCount = (filters: DirectoryFilters): number => Object.keys(activeFilters(filters)).length;

const ENUM_VALUES: Partial<Record<FilterKey, readonly string[]>> = {
  gender: GENDER_OPTIONS.map((option) => option.value),
  practitionerType: PRACTITIONER_TYPE_OPTIONS.map((option) => option.value),
  sessionFormat: Object.keys(SESSION_FORMAT_LABELS),
};

export const toFilterParams = (filters: DirectoryFilters): [FilterKey, string][] =>
  Object.entries(activeFilters(filters)).map(([key, values]) => [key as FilterKey, values.join(",")]);

export const parseFilters = (params: URLSearchParams): DirectoryFilters =>
  Object.fromEntries(
    FILTER_KEYS.flatMap((key) => {
      const allowed = ENUM_VALUES[key];
      const values = [...new Set((params.get(key) ?? "").split(",").map((value) => value.trim()))].filter(
        (value) => value.length > 0 && (!allowed || allowed.includes(value)),
      );

      return values.length > 0 ? [[key, values]] : [];
    }),
  );

/** How far out a searched location looks, matching the API's own default when we send none. */
const SEARCH_RADIUS_METRES = 50_000;

/**
 * Filters plus the searched location, as the API takes them. The location is a filter too: with an
 * origin the endpoint bounds results to the radius and returns them nearest-first, and without one
 * it falls back to the whole directory ordered by experience.
 */
export const toQuery = (
  filters: DirectoryFilters,
  origin?: { lat: number; lng: number } | null,
): ListPractitionersQuery => {
  const active = activeFilters(filters);

  return {
    ...active,
    gender: active.gender as Gender[] | undefined,
    practitionerType: active.practitionerType as PractitionerType[] | undefined,
    sessionFormat: active.sessionFormat as SessionFormat[] | undefined,
    ...(origin ? { lat: origin.lat, lng: origin.lng, radiusMetres: SEARCH_RADIUS_METRES } : {}),
  };
};
